import React, { useEffect, useMemo, useRef, useState } from "react"
import { ChevronDown } from "lucide-react"
import {
    ORDER_FORM_ACCORDION_ANIMATE,
    ORDER_FORM_ACCORDION_TRANSITION_MS,
} from "./orderFormAccordionConstants.js"

const PANEL_EASE = "ease-in-out"
/** Below this status-slot width, show only "(N issues)" — message is too cramped to help. */
const ISSUE_STATUS_MESSAGE_MIN_WIDTH_PX = 200

/** @param {string} label */
function splitIssueStatusLabel(label) {
    const match = String(label ?? "").match(/^(.*?)\s+(\(\d+\s+issues?\))\s*$/)
    if (!match) return null
    return { message: match[1], countLabel: match[2] }
}

/**
 * @param {{
 *   title: string,
 *   isActive?: boolean,
 *   isCompleted?: boolean,
 *   hasErrors?: boolean,
 *   onClick?: () => void,
 *   disabled?: boolean,
 *   statusLabel?: string | null,
 *   children?: React.ReactNode,
 * }} props
 */
export default function Accordion({
    title,
    isActive,
    isCompleted,
    hasErrors = false,
    onClick,
    disabled = false,
    statusLabel = null,
    children,
}) {
    const isControlled = typeof isActive === "boolean"
    const [uncontrolledOpen, setUncontrolledOpen] = useState(true)
    const open = isControlled ? Boolean(isActive) : uncontrolledOpen
    const issueStatusSlotRef = useRef(/** @type {HTMLSpanElement | null} */ (null))
    const [showIssueMessage, setShowIssueMessage] = useState(true)

    const handleHeaderClick = useMemo(() => {
        if (disabled) return undefined
        if (isControlled) return onClick
        return () => setUncontrolledOpen((v) => !v)
    }, [disabled, isControlled, onClick])

    const panelMs = ORDER_FORM_ACCORDION_ANIMATE ? ORDER_FORM_ACCORDION_TRANSITION_MS : 0
    const chevronTransition = ORDER_FORM_ACCORDION_ANIMATE ?
        `transform ${panelMs}ms ${PANEL_EASE}` :
        "none"
    const panelTransition = ORDER_FORM_ACCORDION_ANIMATE ?
        `grid-template-rows ${panelMs}ms ${PANEL_EASE}` :
        "none"

    const showStatus = Boolean(statusLabel) || isCompleted || hasErrors
    const computedStatusLabel = hasErrors ?
        (statusLabel ?? "Needs attention") :
        (statusLabel ?? "✓ Complete")
    const statusClass = hasErrors ?
        "text-danger" :
        (statusLabel ? "text-secondary" : "text-success")
    const issueParts = hasErrors ? splitIssueStatusLabel(computedStatusLabel) : null

    useEffect(() => {
        if (!issueParts) {
            setShowIssueMessage(true)
            return
        }
        const el = issueStatusSlotRef.current
        if (!el || typeof ResizeObserver === "undefined") return

        const update = () => {
            setShowIssueMessage(el.clientWidth >= ISSUE_STATUS_MESSAGE_MIN_WIDTH_PX)
        }
        update()
        const observer = new ResizeObserver(update)
        observer.observe(el)
        return () => observer.disconnect()
    }, [issueParts, computedStatusLabel])

    return (
        <div className={`border rounded mb-3 overflow-hidden${hasErrors ? " border-danger" : ""}`}>
            <button
                type="button"
                onClick={handleHeaderClick}
                disabled={disabled}
                className={`w-100 px-4 py-3 d-flex align-items-center gap-2 ${
                    open ? "bg-primary bg-opacity-10" : "bg-white"
                }`}
                style={{
                    border: "none",
                    cursor: disabled ? "default" : "pointer",
                    opacity: disabled ? 0.65 : 1,
                }}
            >
                <div className="d-flex align-items-center gap-3 min-w-0 flex-grow-1">
                    <span className="fw-semibold fs-5 text-nowrap flex-shrink-0">{title}</span>
                    {showStatus && (
                        issueParts ? (
                            <span
                                ref={issueStatusSlotRef}
                                className={`${statusClass} d-inline-flex align-items-baseline min-w-0 flex-grow-1`}
                                title={computedStatusLabel}
                                style={{ fontSize: "0.875rem", gap: "0.35rem" }}
                            >
                                {showIssueMessage ? (
                                    <span className="text-truncate" style={{ minWidth: 0 }}>{issueParts.message}</span>
                                ) : null}
                                <span className="text-nowrap flex-shrink-0 fw-semibold">{issueParts.countLabel}</span>
                            </span>
                        ) : (
                            <span
                                className={`${statusClass} text-truncate`}
                                title={computedStatusLabel}
                                style={{ fontSize: "0.875rem", minWidth: 0 }}
                            >
                                {computedStatusLabel}
                            </span>
                        )
                    )}
                </div>
                <ChevronDown
                    className="flex-shrink-0"
                    style={{
                        transform: open ? "rotate(180deg)" : "rotate(0deg)",
                        transition: chevronTransition,
                        opacity: disabled ? 0.6 : 1,
                    }}
                />
            </button>
            <div
                style={{
                    display: "grid",
                    gridTemplateRows: open ? "1fr" : "0fr",
                    transition: panelTransition,
                }}
            >
                <div style={{ overflow: "hidden", minHeight: 0 }}>
                    <div className="p-4 border-top">{children}</div>
                </div>
            </div>
        </div>
    )
}
